<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
} from "../../composables/useSeo";
import { usePhoneNumber } from "../../composables/usePhoneNumber";
import { useAnalytics } from "../../composables/useAnalytics";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();
const { servicePrice, serviceLabel, loadPrice } = usePricing();
const displayedServicePrice = servicePrice("construction-debris");
const displayedServiceLabel = serviceLabel("construction-debris");
const quarterTrailerPrice = loadPrice("quarter-trailer");
const halfTrailerPrice = loadPrice("half-trailer");
const fullTrailerPrice = loadPrice("full-trailer");

useSeoBase({
  title:
    "Construction Debris Removal | Junk Guys MN - Heavy Debris & Renovation Cleanup",
  description:
    "Professional construction debris removal services in the Twin Cities. Handle renovation waste, demolition debris, and construction materials. Get your site clean today!",
  canonical: "https://junkguysmn.com/services/construction-debris",
  ogImage: "https://junkguysmn.com/og.jpg",
});

useJsonLd([
  orgNode(),
  localBusinessNode(),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "Services", url: "https://junkguysmn.com/services" },
    {
      name: "Construction Debris Removal",
      url: "https://junkguysmn.com/services/construction-debris",
    },
  ]),
  {
    "@type": "Service",
    name: "Construction Debris Removal",
    description:
      "Professional construction debris and renovation waste removal services",
    provider: {
      "@type": "Organization",
      name: "Junk Guys MN",
    },
    areaServed: {
      "@type": "Place",
      name: "Minneapolis-St. Paul Twin Cities Metro Area",
    },
    serviceType: "Construction Debris Removal",
  },
]);
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Construction Debris Removal
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Professional construction debris removal and renovation cleanup. We
            handle heavy materials and large volumes.
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Overview -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-slate-900 mb-6">
              What We Handle
            </h2>
            <p class="text-lg text-slate-600 mb-6">
              Our construction debris removal service handles all types of
              renovation and construction waste throughout Minneapolis, St.
              Paul, and the Twin Cities. We manage large volumes and heavy
              materials efficiently with professional equipment and responsible
              disposal.
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Drywall & Plaster</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Wood & Lumber</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Tile & Ceramics</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Insulation</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Plumbing Parts</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Electrical Waste</span>
              </div>
            </div>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <h3 class="text-2xl font-bold text-slate-900 mb-6">
              Service Details
            </h3>
            <div class="space-y-4">
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700">{{
                  displayedServiceLabel
                }}</span>
                <span class="text-2xl font-bold text-primary-main">{{
                  displayedServicePrice
                }}</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Typical Duration</span
                >
                <span class="text-lg text-slate-600">2-4 hours</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Heavy Equipment</span
                >
                <span class="text-green-600 font-semibold">Available</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Our Construction Debris Removal
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional service with proper equipment and environmental
            responsibility
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🚛</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Heavy Equipment
            </h3>
            <p class="text-slate-600">
              We have the right equipment to handle heavy construction materials
              and large volumes efficiently.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🧹</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Site Cleanup
            </h3>
            <p class="text-slate-600">
              We don't just haul debris - we clean up the site and leave it
              ready for the next phase.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🌱</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Eco-Friendly
            </h3>
            <p class="text-slate-600">
              We recycle materials when possible and ensure proper disposal of
              construction waste.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Construction Debris Removal Pricing
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Transparent pricing based on volume and material type
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">📦</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Small Project</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ quarterTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">1/4 truck load</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Bathroom remodel</li>
              <li>• Small room demo</li>
              <li>• Basic cleanup</li>
              <li>• 2-3 hours</li>
            </ul>
          </div>
          <div
            class="bg-white rounded-2xl shadow-lg p-8 text-center border-2 border-primary-main"
          >
            <div class="text-4xl mb-4">🏠</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              Medium Project
            </h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ halfTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">1/2 truck load</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Kitchen remodel</li>
              <li>• Multiple rooms</li>
              <li>• Heavy materials</li>
              <li>• 3-4 hours</li>
            </ul>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🏗️</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Large Project</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ fullTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">Full truck load</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Full house demo</li>
              <li>• Commercial space</li>
              <li>• Heavy equipment</li>
              <li>• 4+ hours</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Factors -->
    <section class="bg-white py-16 border-t border-slate-200">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            What Affects Construction Debris Removal Cost?
          </h2>
          <p class="text-lg text-slate-600">
            Our transparent pricing considers these key factors
          </p>
        </div>

        <div class="space-y-4">
          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Volume & Scope</h3>
              <p class="text-slate-600">
                Small bathroom demo vs. full house renovation-the amount of
                debris directly impacts truck loads and labor time.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Material Weight & Type
              </h3>
              <p class="text-slate-600">
                Heavy concrete, tile, and metal weigh more than drywall or
                insulation, affecting disposal costs and load capacity.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Site Access & Safety
              </h3>
              <p class="text-slate-600">
                Stairs, narrow hallways, or multi-story buildings require extra
                care and time to remove materials safely.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Demolition Work</h3>
              <p class="text-slate-600">
                If we're handling demolition in addition to hauling, this adds
                to the project complexity and timeline.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Urgency & Scheduling
              </h3>
              <p class="text-slate-600">
                Need debris cleared ASAP to keep contractors on schedule? We
                offer same-day service when available.
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-10 p-6 bg-primary-main/10 border-l-4 border-primary-main rounded-r-lg"
        >
          <p class="text-lg text-slate-900">
            <strong>Ready for an accurate quote?</strong> Call
            <a
              :href="phoneNumberTel"
              class="font-bold text-primary-main hover:text-primary-dark underline"
              >(612) 424-0609</a
            >
            for a fast estimate. We provide upfront pricing before we start-no
            surprises.
          </p>
        </div>
      </div>
    </section>

    <!-- Why Choose Junk Guys MN -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Junk Guys MN
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional construction debris removal service you can trust
          </p>
        </div>
        <div class="grid md:grid-cols-4 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🏠</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Locally Owned
            </h3>
            <p class="text-slate-600">
              Family-owned business serving the Twin Cities communities
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">⚡</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Same-Day Service
            </h3>
            <p class="text-slate-600">
              Quick response with same-day availability when you need it
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">♻️</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Eco-Friendly
            </h3>
            <p class="text-slate-600">
              We donate and recycle whenever possible to reduce landfill waste
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">💰</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Upfront Pricing
            </h3>
            <p class="text-slate-600">
              Transparent quotes with no hidden fees or surprises
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Areas -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">Service Areas</h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Providing construction debris removal services throughout
            Minneapolis, St. Paul, and surrounding Twin Cities communities
          </p>
        </div>
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/minneapolis/construction-debris"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Minneapolis</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/saint-paul/construction-debris"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Saint Paul</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/bloomington"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Bloomington</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/eden-prairie"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Eden Prairie</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/maple-grove"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Maple Grove</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/brooklyn-park"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Brooklyn Park</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/lakeville"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Lakeville</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/services"
              class="text-slate-700 hover:text-primary-main font-semibold"
              >+ More Areas</NuxtLink
            >
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p class="text-lg text-slate-600">
            Common questions about construction debris removal
          </p>
        </div>
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200 divide-y divide-slate-200"
        >
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  What materials can't you remove?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We cannot remove hazardous materials like asbestos, lead paint,
                or chemicals. These require special handling and disposal
                methods. Contact specialized hazardous waste companies for these
                materials.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you provide dumpsters or containers?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We typically do direct removal rather than providing dumpsters.
                This allows us to handle the debris immediately and clean up the
                site. For very large projects, we can coordinate with dumpster
                services.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How do you handle heavy materials like concrete?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We use proper equipment and techniques to handle heavy materials
                safely. For very heavy items, we may need to break them down or
                use specialized equipment. We'll assess the situation and
                provide the best solution.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How much does construction debris removal cost?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Construction debris removal is {{ displayedServicePrice }}.
                Larger renovations or heavier materials may cost more. Call
                <a
                  :href="phoneNumberTel"
                  class="font-semibold text-primary-main hover:text-primary-dark underline"
                  >(612) 424-0609</a
                >
                for an accurate quote based on your specific construction
                project.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you offer same-day construction debris removal?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Yes! Same-day service is available throughout the Twin Cities to
                keep your construction project on schedule. Contact us early for
                best availability.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do I need to be present during debris removal?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                While we prefer someone is on-site for final approval and to
                show us the debris area, it's not required. Many contractors
                arrange for us to work independently on Minneapolis-St. Paul job
                sites.
              </div>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-7xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Need Construction Debris Removed?
        </h2>
        <p class="text-xl text-white mb-8 max-w-2xl mx-auto">
          Get your construction site clean today. Professional debris removal
          and site cleanup included.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
